// src/runtime/ContentRuntime/platforms/wechat/token.ts
// ============================================
// 微信公众号 Access Token 管理（冻结版本 v1.0）
// ============================================

import { WeChatConfig, WeChatEndpoints } from './config';

interface TokenResponse {
  access_token: string;
  expires_in: number;
  errcode?: number;
  errmsg?: string;
}

/** Token 获取的可选配置覆盖（用于不同配置来源的调用方） */
export interface WeChatTokenOptions {
  /** 应用 ID（不传则使用 WeChatConfig.appId） */
  appId?: string;
  /** 应用密钥（不传则使用 WeChatConfig.appSecret） */
  appSecret?: string;
  /** 代理地址（不传则使用 WeChatConfig.proxyUrl） */
  proxyUrl?: string;
}

// 模块级缓存（单例：整个应用共享同一份 token，避免多实例并发获取导致互踢失效）
let cachedToken: string | null = null;
let tokenExpireAt: number = 0;
// 正在进行中的请求 Promise（并发合并：多个调用同时获取 token 时只发一个请求）
let inflightPromise: Promise<string> | null = null;

export class WeChatTokenManager {
  /**
   * 获取 Access Token（自动缓存 + 并发请求合并）
   *
   * @param options 可选配置覆盖；不传则使用 WeChatConfig 中的环境变量配置
   */
  static async getToken(options?: WeChatTokenOptions): Promise<string> {
    // 命中缓存直接返回
    if (cachedToken && Date.now() < tokenExpireAt) {
      return cachedToken;
    }

    // 并发合并：已有请求在进行中时复用同一个 Promise，避免重复请求
    if (inflightPromise) {
      return inflightPromise;
    }

    inflightPromise = WeChatTokenManager.fetchToken(options);
    try {
      return await inflightPromise;
    } finally {
      inflightPromise = null;
    }
  }

  /**
   * 实际发起 Token 请求（私有）
   */
  private static async fetchToken(options?: WeChatTokenOptions): Promise<string> {
    const appId = options?.appId || WeChatConfig.appId;
    const appSecret = options?.appSecret || WeChatConfig.appSecret;
    const proxyUrl = options?.proxyUrl || WeChatConfig.proxyUrl;

    // P12 短板4/5/8：AppSecret 由服务端 Vault 托管，前端 URL 不再携带密钥。
    // 后端代理(/wechat-api/cgi-bin/token)会在 secret/appid 缺失时代填。
    const url =
      `${proxyUrl}${WeChatEndpoints.token}?grant_type=client_credential` +
      (appId ? `&appid=${encodeURIComponent(appId)}` : '') +
      (appSecret ? `&secret=${encodeURIComponent(appSecret)}` : '');

    console.log('[WeChat] 请求 Token (通过代理, 密钥由后端代填)');

    const response = await fetch(url);
    const data = (await response.json()) as TokenResponse;

    if (data.errcode) {
      throw new Error(`微信 Token 获取失败: ${data.errmsg} (${data.errcode})`);
    }

    cachedToken = data.access_token;
    tokenExpireAt = Date.now() + (data.expires_in - 200) * 1000;

    console.log('[WeChat] Access Token 刷新成功');
    return cachedToken;
  }

  /**
   * 清除缓存（用于测试）
   */
  static clearCache(): void {
    cachedToken = null;
    tokenExpireAt = 0;
    inflightPromise = null;
  }
}
